import { useState } from 'react';
import {
  Play,
  Check,
  X,
  Plus,
  Search,
  Save,
  Trash2,
  Edit2,
  Settings,
  ArrowLeft,
  ChevronDown,
  AlertTriangle,
  CheckCircle2,
  Info,
  Loader2,
  Mail,
  Package,
  CreditCard,
  Truck,
  FileText,
  User,
  Eye,
} from 'lucide-react';
import { Subsection } from '../components/Subsection';

// ============================================================================
// Kolory
// ============================================================================

interface Swatch {
  token: string;
  className: string;
  hex: string;
  usage: string;
  legacy?: string;
}

interface SwatchGroup {
  title: string;
  description: string;
  swatches: Swatch[];
  theme?: 'surface' | 'text' | 'accent' | 'status' | 'border';
}

const SWATCH_GROUPS: SwatchGroup[] = [
  {
    title: 'Surface',
    description: 'Tła: body, karty, panele, hover states. Od najciemniejszego do najjaśniejszego.',
    theme: 'surface',
    swatches: [
      { token: 'surface.base',    className: 'bg-surface-base',    hex: '#16161c', usage: 'Body background',              legacy: 'dark-900' },
      { token: 'surface.raised',  className: 'bg-surface-raised',  hex: '#1e1e26', usage: 'Karty, panele, modale',        legacy: 'dark-800' },
      { token: 'surface.hover',   className: 'bg-surface-hover',   hex: '#26262f', usage: 'Hover na surface',             legacy: 'dark-700' },
      { token: 'surface.muted',   className: 'bg-surface-muted',   hex: '#2e2e38', usage: 'Drugi poziom hover, scrollbar', legacy: 'dark-600' },
      { token: 'surface.overlay', className: 'bg-surface-overlay', hex: '#363642', usage: 'Tooltips, context menu, dropdowny', legacy: 'dark-500' },
      { token: 'surface.bright',  className: 'bg-surface-bright',  hex: '#464654', usage: 'Rzadko - najjaśniejszy surface',    legacy: 'dark-400' },
    ],
  },
  {
    title: 'Text',
    description: 'Hierarchia tekstu. Od primary (white) do disabled (gray-500). Poniżej disabled nie schodzimy.',
    theme: 'text',
    swatches: [
      { token: 'text.primary',   className: 'bg-text-primary',   hex: '#ffffff', usage: 'Główny tekst',                   legacy: 'text-text-primary' },
      { token: 'text.secondary', className: 'bg-text-secondary', hex: '#d1d5db', usage: 'Drugi poziom, labels, opisy',    legacy: 'text-text-secondary' },
      { token: 'text.muted',     className: 'bg-text-muted',     hex: '#9ca3af', usage: 'Placeholdery, captions, meta',   legacy: 'text-text-muted' },
      { token: 'text.disabled',  className: 'bg-text-disabled',  hex: '#6b7280', usage: 'Disabled inputs, szare dekoracje', legacy: 'text-text-disabled' },
    ],
  },
  {
    title: 'Accent',
    description: 'Kolory marki. Stonowane niebieskie - nazwy neon-* historycznie, faktycznie to blue tones.',
    theme: 'accent',
    swatches: [
      { token: 'accent.primary',   className: 'bg-accent-primary',   hex: '#4a9eff', usage: 'CTA, linki, focus, primary button', legacy: 'neon-cyan' },
      { token: 'accent.secondary', className: 'bg-accent-secondary', hex: '#6b8cce', usage: 'Akcent uzupełniający, gradienty',  legacy: 'neon-purple' },
      { token: 'accent.tertiary',  className: 'bg-accent-tertiary',  hex: '#7a8fba', usage: 'Rzadki akcent',                    legacy: 'neon-pink' },
      { token: 'accent.deep',      className: 'bg-accent-deep',      hex: '#1e3a5f', usage: 'Hover na accent, gradient end',    legacy: 'neon-navy' },
      { token: 'accent.info',      className: 'bg-accent-info',      hex: '#3b82f6', usage: 'Info accent, standard blue',       legacy: 'neon-blue' },
    ],
  },
  {
    title: 'Status',
    description: 'Feedback: success / warning / danger / info. Każdy ma DEFAULT + tło + border (opacity warianty).',
    theme: 'status',
    swatches: [
      { token: 'success', className: 'bg-success', hex: '#10b981', usage: 'Zapisano, operacja OK, badge-success' },
      { token: 'warning', className: 'bg-warning', hex: '#f59e0b', usage: 'Ostrzeżenia, pending states' },
      { token: 'danger',  className: 'bg-danger',  hex: '#ef4444', usage: 'Błędy, destructive actions, delete' },
      { token: 'info',    className: 'bg-info',    hex: '#3b82f6', usage: 'Info messages, notifications' },
    ],
  },
  {
    title: 'Border',
    description: 'Granice elementów. Semantyczne natężenia: subtle -> default -> strong -> accent.',
    theme: 'border',
    swatches: [
      { token: 'border.subtle',  className: 'bg-border-subtle',  hex: 'rgba(255,255,255,0.06)', usage: 'Default delikatny border' },
      { token: 'border.DEFAULT', className: 'bg-border',         hex: 'rgba(255,255,255,0.10)', usage: 'Widoczny border (kart, inputów)' },
      { token: 'border.strong',  className: 'bg-border-strong',  hex: 'rgba(255,255,255,0.15)', usage: 'Mocniejszy border (wyróżnione)' },
      { token: 'border.accent',  className: 'bg-border-accent',  hex: 'rgba(74,158,255,0.30)',  usage: 'Accent border (focus, active)' },
    ],
  },
];

function SwatchCard({ swatch, theme }: { swatch: Swatch; theme?: SwatchGroup['theme'] }) {
  const isText = theme === 'text';
  const isBorder = theme === 'border';
  return (
    <div className="rounded-xl border border-border bg-surface-raised/40 overflow-hidden">
      <div
        className={`relative h-20 ${swatch.className} ${isBorder ? 'bg-surface-base' : ''} ${isText ? 'flex items-center justify-center' : ''}`}
        style={isBorder ? { boxShadow: `inset 0 0 0 8px ${swatch.hex}` } : undefined}
      >
        {isText && <span className="text-xs font-medium text-surface-base">Aa — próbka</span>}
        {swatch.legacy && (
          <span className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded bg-black/40 backdrop-blur-sm text-[9px] uppercase tracking-wider text-text-secondary font-medium">
            {swatch.legacy}
          </span>
        )}
      </div>
      <div className="p-3 space-y-1.5">
        <div className="flex items-baseline justify-between gap-2">
          <code className="text-xs font-mono text-text-primary font-semibold">{swatch.token}</code>
          <code className="text-[10px] font-mono text-text-muted truncate" title={swatch.hex}>{swatch.hex}</code>
        </div>
        <p className="text-[11px] text-text-muted leading-snug">{swatch.usage}</p>
      </div>
    </div>
  );
}

export function ColorsSection() {
  return (
    <div className="space-y-8">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        Semantyczne tokeny kolorów zdefiniowane w <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">tailwind.config.js</code>.
        Używaj ich jako klas Tailwind (<code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">bg-surface-raised</code>,{' '}
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">text-text-secondary</code>,{' '}
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">border-border</code>). Stare aliasy (neon-*, dark-*) zostają jako legacy
        do czasu pełnej migracji - są oznaczone w prawym górnym rogu swatcha.
      </p>

      {SWATCH_GROUPS.map((group, idx) => (
        <Subsection key={group.title} n={idx + 1} title={group.title} description={group.description}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {group.swatches.map(s => <SwatchCard key={s.token} swatch={s} theme={group.theme} />)}
          </div>
        </Subsection>
      ))}
    </div>
  );
}

// ============================================================================
// Typografia
// ============================================================================

interface SizeRow {
  className: string;
  px: string;
  lineHeight: string;
  usage: string;
  note?: string;
}

const SIZE_SCALE: SizeRow[] = [
  { className: 'text-xxs',  px: '10px', lineHeight: '14px', usage: 'Micro labels, uppercase captions, timestamps' },
  { className: 'text-xs',   px: '11px', lineHeight: '16px', usage: 'Caption, badge, meta-info', note: 'Override Tailwind default (12px) na 11px dla ciaśniejszej skali' },
  { className: 'text-sm',   px: '14px', lineHeight: '20px', usage: 'Body default (większość UI)' },
  { className: 'text-base', px: '16px', lineHeight: '24px', usage: 'Body w długich tekstach (form help, opisy)' },
  { className: 'text-lg',   px: '18px', lineHeight: '28px', usage: 'Podtytuły sekcji' },
  { className: 'text-xl',   px: '20px', lineHeight: '28px', usage: 'H3, karty tytuł' },
  { className: 'text-2xl',  px: '24px', lineHeight: '32px', usage: 'H2' },
  { className: 'text-3xl',  px: '30px', lineHeight: '36px', usage: 'H1 strony' },
  { className: 'text-4xl',  px: '36px', lineHeight: '40px', usage: 'Display (hero, dashboards)' },
];

const WEIGHT_SCALE = [
  { className: 'font-light',    weight: 300, label: 'Light',     usage: 'Rzadko - lżejsze akcenty w display' },
  { className: 'font-normal',   weight: 400, label: 'Regular',   usage: 'Body default' },
  { className: 'font-medium',   weight: 500, label: 'Medium',    usage: 'Emphasis w body, labels' },
  { className: 'font-semibold', weight: 600, label: 'Semibold',  usage: 'Buttons, subheadings, tytuły kart' },
  { className: 'font-bold',     weight: 700, label: 'Bold',      usage: 'H1/H2/H3, display' },
  { className: 'font-extrabold',weight: 800, label: 'Extrabold', usage: 'Bardzo rzadko - hero text' },
];

const TRACKING_SAMPLES = [
  { className: 'tracking-tighter', label: 'tighter', usage: 'Display' },
  { className: 'tracking-tight',   label: 'tight',   usage: 'H1/H2' },
  { className: 'tracking-normal',  label: 'normal',  usage: 'Body default' },
  { className: 'tracking-wide',    label: 'wide',    usage: 'Buttons, labels' },
  { className: 'tracking-wider',   label: 'wider',   usage: 'Uppercase captions' },
  { className: 'tracking-widest',  label: 'widest',  usage: 'Uppercase display' },
];

export function TypographySection() {
  return (
    <div className="space-y-10">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        Skala: <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">text-xxs</code> (10px) dla micro labels / timestamps,
        przez <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">text-sm</code> (14px) body default,
        do <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">text-4xl</code> (36px) display. Custom inline{' '}
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">text-[Xpx]</code> odradzany - używaj skali.
      </p>

      <Subsection n={1} title="Rodzina fontów" description={<><code className="px-1 rounded bg-surface-raised text-text-secondary">Inter</code> ładowany z Google Fonts w <code className="px-1 rounded bg-surface-raised text-text-secondary">index.css</code>. Fallback: system-ui, sans-serif.</>}>
        <div className="rounded-xl border border-border bg-surface-raised/40 p-6">
          <div className="grid grid-cols-1 md:grid-cols-[160px,1fr] gap-6 items-center">
            <div>
              <div className="text-xxs uppercase tracking-wider text-text-disabled mb-1">Primary</div>
              <div className="text-2xl font-bold text-text-primary">Inter</div>
              <div className="text-xs text-text-muted mt-1">wagi 300-800</div>
            </div>
            <div className="space-y-1 font-sans">
              <p className="text-3xl font-bold text-text-primary">The quick brown fox</p>
              <p className="text-xl text-text-primary">jumps over the lazy dog</p>
              <p className="text-sm text-text-muted">AaBbCcDdEeFfGgHh 0123456789 !@#$%^&*()</p>
              <p className="text-xs text-text-disabled">Zażółć gęślą jaźń — polskie ogonki OK</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-border-subtle">
            <div className="text-xxs uppercase tracking-wider text-text-disabled mb-1">Fallback (system-ui)</div>
            <p className="text-base" style={{ fontFamily: 'system-ui, sans-serif' }}>
              Tak wygląda system UI font gdy Inter się nie załaduje - zapasowy dla offline / slow connection.
            </p>
          </div>
        </div>
      </Subsection>

      <Subsection n={2} title="Rozmiary" description="Klasa → rozmiar → preview (Aa + lorem ipsum) → użycie.">
        <div className="rounded-xl border border-border bg-surface-raised/40 overflow-hidden divide-y divide-border-subtle">
          {SIZE_SCALE.map(row => (
            <div key={row.className} className="grid grid-cols-[120px,90px,1fr,220px] gap-4 items-center px-4 py-3">
              <code className="text-xs font-mono text-text-primary font-semibold">{row.className}</code>
              <span className="text-xs font-mono text-text-muted">{row.px} / {row.lineHeight}</span>
              <span className={`${row.className} text-text-primary font-medium truncate`}>
                Aa — Lorem ipsum dolor sit amet, consectetur adipiscing elit
              </span>
              <div className="flex flex-col min-w-0">
                <span className="text-xs text-text-secondary leading-snug">{row.usage}</span>
                {row.note && <span className="text-[10px] text-accent-primary/80 mt-0.5">{row.note}</span>}
              </div>
            </div>
          ))}
        </div>
      </Subsection>

      <Subsection n={3} title="Wagi" description="Inter wspiera 300-800. W projekcie używamy głównie Regular/Medium/Semibold/Bold.">
        <div className="rounded-xl border border-border bg-surface-raised/40 overflow-hidden divide-y divide-border-subtle">
          {WEIGHT_SCALE.map(w => (
            <div key={w.className} className="grid grid-cols-[140px,60px,1fr,1fr] gap-4 items-center px-4 py-3">
              <code className="text-xs font-mono text-text-primary font-semibold">{w.className}</code>
              <span className="text-xs font-mono text-text-muted">{w.weight}</span>
              <span className={`${w.className} text-xl text-text-primary`}>{w.label} - EcomFlow</span>
              <span className="text-xs text-text-muted leading-snug">{w.usage}</span>
            </div>
          ))}
        </div>
      </Subsection>

      <Subsection n={4} title="Line-height (dla body)" description={<>Dłuższe bloki tekstu: <code className="px-1 rounded bg-surface-raised text-text-secondary text-[11px]">leading-relaxed</code> (1.625) lub <code className="px-1 rounded bg-surface-raised text-text-secondary text-[11px]">leading-7</code> (28px).</>}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { cls: 'leading-tight',    label: '1.25' },
            { cls: 'leading-normal',   label: '1.5 (default root)' },
            { cls: 'leading-relaxed',  label: '1.625' },
          ].map(lh => (
            <div key={lh.cls} className="rounded-xl border border-border bg-surface-raised/40 p-4">
              <div className="flex items-baseline justify-between mb-2">
                <code className="text-xs font-mono text-text-primary font-semibold">{lh.cls}</code>
                <span className="text-[10px] font-mono text-text-muted">{lh.label}</span>
              </div>
              <p className={`${lh.cls} text-sm text-text-secondary`}>
                EcomFlow to operatorski SaaS do zarządzania zamówieniami. Tekst próbny pokazuje jak różne line-heighty wpływają na czytelność body text.
              </p>
            </div>
          ))}
        </div>
      </Subsection>

      <Subsection n={5} title="Letter-spacing (tracking)" description={<>W praktyce używamy: <code className="px-1 rounded bg-surface-raised text-text-secondary text-[11px]">tracking-wider</code> dla uppercase captions.</>}>
        <div className="rounded-xl border border-border bg-surface-raised/40 overflow-hidden divide-y divide-border-subtle">
          {TRACKING_SAMPLES.map(t => (
            <div key={t.className} className="grid grid-cols-[140px,1fr,1fr] gap-4 items-center px-4 py-3">
              <code className="text-xs font-mono text-text-primary font-semibold">{t.className}</code>
              <span className={`${t.className} text-xs text-text-secondary uppercase`}>Design System</span>
              <span className="text-xs text-text-muted">{t.usage}</span>
            </div>
          ))}
        </div>
      </Subsection>

      <Subsection n={6} title="Hierarchia w praktyce" description="Jak rozmiary i wagi współpracują ze sobą na typowej stronie admina.">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-6 space-y-3">
          <div>
            <span className="text-xxs uppercase tracking-wider text-text-disabled font-medium">Ustawienia</span>
          </div>
          <h1 className="text-3xl font-bold text-text-primary">Integracja z WooCommerce</h1>
          <p className="text-base text-text-secondary">
            Połącz swój sklep, żeby automatycznie synchronizować zamówienia, statusy paczek i stany magazynowe.
          </p>
          <div className="pt-4">
            <h2 className="text-xl font-semibold text-text-primary mb-1">Klucze API</h2>
            <p className="text-sm text-text-muted">Consumer Key i Secret znajdziesz w panelu WooCommerce → Settings → Advanced → REST API.</p>
          </div>
          <div>
            <label className="text-xs font-medium text-text-muted uppercase tracking-wider">Consumer Key</label>
          </div>
        </div>
      </Subsection>
    </div>
  );
}

// ============================================================================
// Spacing
// ============================================================================

const SPACING_SCALE = [
  { cls: 'p-1',  px: '4px' },
  { cls: 'p-2',  px: '8px' },
  { cls: 'p-3',  px: '12px' },
  { cls: 'p-4',  px: '16px' },
  { cls: 'p-5',  px: '20px' },
  { cls: 'p-6',  px: '24px' },
  { cls: 'p-8',  px: '32px' },
  { cls: 'p-10', px: '40px' },
  { cls: 'p-12', px: '48px' },
];

const SPACING_SEMANTIC = [
  { token: 'field-x', className: 'w-field-x', px: '16px', usage: 'Padding poziomy inputów/buttonów (md)' },
  { token: 'field-y', className: 'w-field-y', px: '10px', usage: 'Padding pionowy inputów/buttonów (md)' },
  { token: 'card',    className: 'w-card',    px: '24px', usage: 'Padding kart (klasa: p-card)' },
  { token: 'section', className: 'w-section', px: '32px', usage: 'Odstęp między sekcjami (klasa: gap-section, space-y-section)' },
];

export function SpacingSection() {
  return (
    <div className="space-y-8">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        Tailwind default scale (4px base) jest źródłem prawdy. Trzymaj się <strong>4/8/12/16/24/32</strong> -
        nie wymyślaj <code className="px-1 rounded bg-surface-raised text-text-secondary">p-5</code> albo{' '}
        <code className="px-1 rounded bg-surface-raised text-text-secondary">gap-7</code>. Plus semantyczne aliasy (<code className="px-1 rounded bg-surface-raised text-text-secondary">field-x</code>,{' '}
        <code className="px-1 rounded bg-surface-raised text-text-secondary">card</code>,{' '}
        <code className="px-1 rounded bg-surface-raised text-text-secondary">section</code>).
      </p>

      <Subsection n={1} title="Skala">
        <div className="space-y-2">
          {SPACING_SCALE.map(s => (
            <div key={s.cls} className="flex items-center gap-4">
              <code className="text-xs font-mono text-text-primary font-semibold w-16">{s.cls}</code>
              <span className="text-xs font-mono text-text-muted w-14">{s.px}</span>
              <div className="h-4 bg-accent-primary/60 rounded-sm" style={{ width: s.px }} />
            </div>
          ))}
        </div>
      </Subsection>

      <Subsection n={2} title="Semantyczne aliasy">
        <div className="rounded-xl border border-border bg-surface-raised/40 overflow-hidden divide-y divide-border-subtle">
          {SPACING_SEMANTIC.map(s => (
            <div key={s.token} className="grid grid-cols-[120px,80px,1fr,120px] gap-4 items-center px-4 py-3">
              <code className="text-xs font-mono text-text-primary font-semibold">{s.token}</code>
              <span className="text-xs font-mono text-text-muted">{s.px}</span>
              <span className="text-xs text-text-secondary">{s.usage}</span>
              <div className="flex items-center justify-end">
                <div className={`${s.className} h-4 bg-accent-primary/60 rounded-sm`} style={{ width: s.px }} />
              </div>
            </div>
          ))}
        </div>
      </Subsection>
    </div>
  );
}

// ============================================================================
// Radius
// ============================================================================

const RADIUS_SCALE = [
  { cls: 'rounded-sm',   px: '2px',   usage: 'Small elements, rzadko' },
  { cls: 'rounded',      px: '4px',   usage: 'Rzadko - small inputs' },
  { cls: 'rounded-md',   px: '6px',   usage: 'Inputs drobne, tagi' },
  { cls: 'rounded-lg',   px: '8px',   usage: 'Secondary buttons, badges' },
  { cls: 'rounded-xl',   px: '12px',  usage: 'Default: buttons, inputs, karty, modale', primary: true },
  { cls: 'rounded-2xl',  px: '16px',  usage: 'Większe kontenery (.card, .table-container)', primary: true },
  { cls: 'rounded-3xl',  px: '24px',  usage: 'Rzadko - hero sections' },
  { cls: 'rounded-full', px: '9999px', usage: 'Pill badges, avatars' },
];

const RADIUS_SEMANTIC = [
  { token: 'rounded-btn',   maps: 'rounded-xl (12px)', usage: 'Button default' },
  { token: 'rounded-input', maps: 'rounded-xl (12px)', usage: 'Input spójny z buttonem' },
  { token: 'rounded-card',  maps: 'rounded-2xl (16px)', usage: 'Karta' },
  { token: 'rounded-badge', maps: 'rounded-full',       usage: 'Pill badge' },
];

export function RadiusSection() {
  return (
    <div className="space-y-8">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        Projekt jest "obły" - dominuje <code className="px-1 rounded bg-surface-raised text-text-secondary">rounded-xl</code> (12px)
        i <code className="px-1 rounded bg-surface-raised text-text-secondary">rounded-2xl</code> (16px). Plus semantyczne aliasy (<code className="px-1 rounded bg-surface-raised text-text-secondary">rounded-btn</code>,{' '}
        <code className="px-1 rounded bg-surface-raised text-text-secondary">rounded-card</code>) dla czytelności.
      </p>

      <Subsection n={1} title="Skala">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {RADIUS_SCALE.map(r => (
            <div key={r.cls} className={`${r.primary ? 'ring-1 ring-accent-primary/30' : ''} rounded-xl border border-border bg-surface-raised/40 p-4`}>
              <div className={`${r.cls} h-16 bg-gradient-to-br from-accent-primary/40 to-accent-secondary/40 mb-3`} />
              <div className="flex items-baseline justify-between mb-1">
                <code className="text-xs font-mono text-text-primary font-semibold">{r.cls}</code>
                <span className="text-[10px] font-mono text-text-muted">{r.px}</span>
              </div>
              <p className="text-[11px] text-text-muted leading-snug">{r.usage}</p>
            </div>
          ))}
        </div>
      </Subsection>

      <Subsection n={2} title="Semantyczne aliasy">
        <div className="rounded-xl border border-border bg-surface-raised/40 overflow-hidden divide-y divide-border-subtle">
          {RADIUS_SEMANTIC.map(r => (
            <div key={r.token} className="grid grid-cols-[160px,180px,1fr] gap-4 items-center px-4 py-3">
              <code className="text-xs font-mono text-text-primary font-semibold">{r.token}</code>
              <span className="text-xs font-mono text-text-muted">→ {r.maps}</span>
              <span className="text-xs text-text-secondary">{r.usage}</span>
            </div>
          ))}
        </div>
      </Subsection>
    </div>
  );
}

// ============================================================================
// Shadows
// ============================================================================

const SHADOWS = [
  { cls: 'shadow-sm',   usage: 'Subtelna elevation' },
  { cls: 'shadow',      usage: 'Default elevation' },
  { cls: 'shadow-md',   usage: 'Cards, dropdowns' },
  { cls: 'shadow-lg',   usage: 'Sticky save bar, modals' },
  { cls: 'shadow-xl',   usage: 'Floating overlays' },
  { cls: 'shadow-2xl',  usage: 'Hero elements' },
];

const SHADOWS_NEON = [
  { cls: 'shadow-neon-cyan',   usage: 'Focus, aktywne CTA (neon-cyan glow)' },
  { cls: 'shadow-neon-purple', usage: 'Rzadko (neon-purple glow)' },
  { cls: 'shadow-neon-pink',   usage: 'Rzadko (neon-pink glow)' },
  { cls: 'shadow-glass',       usage: 'Karty (.card), modale - 0 8px 32px rgba(0,0,0,0.3)' },
];

export function ShadowsSection() {
  return (
    <div className="space-y-8">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        Dwie kategorie: standardowy Tailwind (<code className="px-1 rounded bg-surface-raised text-text-secondary">shadow-sm</code>...<code className="px-1 rounded bg-surface-raised text-text-secondary">shadow-2xl</code>)
        dla elevation i neon glow (<code className="px-1 rounded bg-surface-raised text-text-secondary">shadow-neon-*</code>)
        dla akcentów marki. Na ciemnym tle neony działają subtelnie - 15% opacity.
      </p>

      <Subsection n={1} title="Tailwind elevation">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {SHADOWS.map(s => (
            <div key={s.cls} className="bg-surface-base/40 rounded-xl p-8">
              <div className={`${s.cls} bg-surface-hover rounded-lg h-16 flex items-center justify-center`}>
                <code className="text-xs font-mono text-text-primary font-semibold">{s.cls}</code>
              </div>
              <p className="text-[11px] text-text-muted mt-3 leading-snug">{s.usage}</p>
            </div>
          ))}
        </div>
      </Subsection>

      <Subsection n={2} title="Neon glow + glass">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {SHADOWS_NEON.map(s => (
            <div key={s.cls} className="bg-surface-base/40 rounded-xl p-8">
              <div className={`${s.cls} bg-surface-raised rounded-lg h-16 flex items-center justify-center border border-border`}>
                <code className="text-xs font-mono text-text-primary font-semibold">{s.cls}</code>
              </div>
              <p className="text-[11px] text-text-muted mt-3 leading-snug">{s.usage}</p>
            </div>
          ))}
        </div>
      </Subsection>
    </div>
  );
}

// ============================================================================
// Motion
// ============================================================================

const MOTION_DURATIONS = [
  { cls: 'duration-fast',   ms: '150ms', usage: 'Hover, focus - szybka reakcja' },
  { cls: 'duration-normal', ms: '250ms', usage: 'Enter/exit - żeby user zauważył' },
  { cls: 'duration-slow',   ms: '400ms', usage: 'Decorative - nie spieszy się' },
];

const MOTION_EASING = [
  { cls: 'ease-smooth',    fn: 'cubic-bezier(0.4, 0, 0.2, 1)',     usage: 'Material default - dla większości interakcji' },
  { cls: 'ease-bounce-in', fn: 'cubic-bezier(0.34, 1.56, 0.64, 1)', usage: 'Lekki overshoot - pojawianie się elementów' },
];

const ANIMATIONS = [
  { cls: 'animate-fade-in',       usage: 'Toast, inline confirmations (0.5s)' },
  { cls: 'animate-scale-in',      usage: 'Modal enter (0.3s)' },
  { cls: 'animate-slide-up',      usage: 'Content enter (0.5s)' },
  { cls: 'animate-slide-down',    usage: 'Dropdown (0.3s)' },
  { cls: 'animate-slide-in-right',usage: 'Side panel enter (0.3s)' },
  { cls: 'animate-shake',         usage: 'Form validation error (0.5s)' },
  { cls: 'animate-pulse-slow',    usage: 'Skeleton, loading hints (4s loop)' },
  { cls: 'animate-glow',          usage: 'Subtle CTA pulse (2s loop)' },
];

function AnimationCard({ cls, usage }: { cls: string; usage: string }) {
  const [playKey, setPlayKey] = useState(0);
  return (
    <div className="rounded-xl border border-border bg-surface-raised/40 p-4 overflow-hidden">
      <div
        key={`${cls}-${playKey}`}
        className={`${cls} bg-gradient-to-br from-accent-primary/30 to-accent-secondary/30 rounded-lg h-16 mb-3 flex items-center justify-center`}
      >
        <span className="text-[10px] font-mono text-text-primary">preview</span>
      </div>
      <div className="flex items-center justify-between gap-2 mb-1">
        <code className="text-xs font-mono text-text-primary font-semibold truncate">{cls}</code>
        <button
          onClick={() => setPlayKey(k => k + 1)}
          className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-lg bg-accent-primary/10 text-accent-primary border border-accent-primary/20 hover:bg-accent-primary/20 transition-colors"
          aria-label={`Odtwórz ${cls}`}
          title="Odtwórz"
        >
          <Play className="w-3 h-3 fill-current" />
        </button>
      </div>
      <p className="text-[11px] text-text-muted leading-snug">{usage}</p>
    </div>
  );
}

export function MotionSection() {
  const [playAllKey, setPlayAllKey] = useState(0);
  return (
    <div className="space-y-8">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        <strong>Hover/interaction:</strong> <code className="px-1 rounded bg-surface-raised text-text-secondary">duration-fast</code> (150ms).{' '}
        <strong>Enter/exit:</strong> <code className="px-1 rounded bg-surface-raised text-text-secondary">duration-normal</code> (250ms).{' '}
        Custom easing: <code className="px-1 rounded bg-surface-raised text-text-secondary">ease-smooth</code>{' '}
        i <code className="px-1 rounded bg-surface-raised text-text-secondary">ease-bounce-in</code>. Respektuj{' '}
        <code className="px-1 rounded bg-surface-raised text-text-secondary">motion-reduce:</code> dla accessibility.
      </p>

      <Subsection n={1} title="Duration" description="Hover na kartę - obserwuj różnicę w szybkości reakcji.">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {MOTION_DURATIONS.map(d => (
            <div
              key={d.cls}
              className={`group rounded-xl border border-border bg-surface-raised/40 p-4 transition-all ${d.cls} ease-smooth hover:bg-accent-primary/10 hover:border-accent-primary/30 hover:-translate-y-1`}
            >
              <code className="text-xs font-mono text-text-primary font-semibold">{d.cls}</code>
              <span className="ml-2 text-[10px] font-mono text-text-muted">{d.ms}</span>
              <p className="text-[11px] text-text-muted mt-2 leading-snug">{d.usage}</p>
            </div>
          ))}
        </div>
      </Subsection>

      <Subsection n={2} title="Easing (timing function)" description="Hover - obserwuj charakterystykę ruchu.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {MOTION_EASING.map(e => (
            <div
              key={e.cls}
              className={`rounded-xl border border-border bg-surface-raised/40 p-4 transition-transform duration-normal ${e.cls} hover:translate-x-4`}
            >
              <code className="text-xs font-mono text-text-primary font-semibold">{e.cls}</code>
              <p className="text-[10px] font-mono text-text-muted mt-1 truncate">{e.fn}</p>
              <p className="text-[11px] text-text-muted mt-2 leading-snug">{e.usage}</p>
            </div>
          ))}
        </div>
      </Subsection>

      <Subsection
        n={3}
        title="Animacje"
        description="Każda karta ma własny przycisk odtworzenia. Górny button renderuje wszystkie naraz."
        actions={
          <button
            onClick={() => setPlayAllKey(k => k + 1)}
            className="text-xs px-3 py-1.5 rounded-lg bg-accent-primary/20 text-accent-primary border border-accent-primary/30 hover:bg-accent-primary/30 transition-colors flex items-center gap-1.5"
          >
            <Play className="w-3 h-3 fill-current" /> Odtwórz wszystkie
          </button>
        }
      >
        <div key={playAllKey} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {ANIMATIONS.map(a => <AnimationCard key={a.cls} cls={a.cls} usage={a.usage} />)}
        </div>
      </Subsection>
    </div>
  );
}

// ============================================================================
// Ikony — Lucide React standardy
// ============================================================================

const ICON_SIZES: Array<{ cls: string; px: string; usage: string; example: typeof Check }> = [
  { cls: 'w-3 h-3',     px: '12px', usage: 'Inline w tekście, status indicators (kropka), znaczniki w tabelach, kafelki gęste',  example: Check },
  { cls: 'w-3.5 h-3.5', px: '14px', usage: 'Mini buttony, sticky save bar, badge content, footer akcje',                          example: Save },
  { cls: 'w-4 h-4',     px: '16px', usage: 'Buttony domyślne, inputy, dropdown chevron, navigation tabs — DEFAULT',               example: Edit2 },
  { cls: 'w-5 h-5',     px: '20px', usage: 'Page header, większe buttony, sidebar nav items, modal close',                        example: Settings },
  { cls: 'w-6 h-6',     px: '24px', usage: 'Hero areas, big actions, empty state icons, headery sekcji',                          example: Mail },
];

const ICON_COLORS: Array<{ cls: string; usage: string; example: typeof Check }> = [
  { cls: 'text-text-primary',   usage: 'Główna ikona w nagłówku, aktywny tab, wybrana opcja',                example: User },
  { cls: 'text-text-secondary', usage: 'Standardowe ikony obok labelu, mniej eksponowane akcje',             example: Search },
  { cls: 'text-text-muted',     usage: 'Pomocnicze ikony w opisach, sekcje header w sidebar',                example: Info },
  { cls: 'text-text-disabled',  usage: 'Wyłączone akcje, brak danego stanu (np. brak shipmentu w OrderRow)', example: Package },
  { cls: 'text-accent-primary', usage: 'Główne CTA, aktywne stany, primary buttony, ważne ikony statusowe',  example: Plus },
  { cls: 'text-success',        usage: 'Sukces, opłacone, dostarczone, zatwierdzone',                        example: CheckCircle2 },
  { cls: 'text-warning',        usage: 'Ostrzeżenie, oczekiwanie, częściowo zrealizowane',                   example: AlertTriangle },
  { cls: 'text-danger',         usage: 'Błędy, usuwanie, anulowane, czarna lista',                           example: Trash2 },
  { cls: 'text-info',           usage: 'Informacje neutralne, w trakcie realizacji',                         example: Info },
];

const ICON_USAGE_RULES: Array<{ context: string; size: string; example: typeof Check; do: string; dont: string }> = [
  { context: 'Button (domyślny)',    size: 'w-4 h-4', example: Save,         do: 'Pojedyncza ikona przed labelem (gap-2). Stroke domyślny Lucide.',                          dont: 'Nie skaluj per button — trzymaj się skali.' },
  { context: 'Button (mini / sm)',   size: 'w-3.5 h-3.5', example: Edit2,    do: 'Sticky save bar, footery modali, tight buttony.',                                          dont: 'Nie używaj w-3 — za małe na klikalność.' },
  { context: 'Page header',          size: 'w-5 h-5', example: Settings,     do: 'Jedna ikona na początku H1, ten sam kolor co tytuł.',                                      dont: 'Nie więcej niż jedna ikona w nagłówku.' },
  { context: 'Status indicator',     size: 'w-3 h-3', example: CreditCard,   do: 'Cluster 3-4 ikon w wierszu (np. OrderRow: payment/shipment/invoice). Bez tekstu obok.',    dont: 'Nie dopisuj nazwy obok — title attribute wystarczy.' },
  { context: 'Empty state',          size: 'w-6 h-6', example: Mail,         do: 'Wycentrowana, w gradient/border kafelku, jedna ikona w EmptyState component.',              dont: 'Nie używaj kolorowych ikon na białym tle bez kontekstu.' },
  { context: 'Loading',              size: 'w-4 h-4', example: Loader2,      do: 'Zawsze z `animate-spin`. Ten sam rozmiar co towarzysząca ikona/tekst.',                    dont: 'Nie mieszaj różnych spinnerów (Loader2 obowiązkowy).' },
  { context: 'Tabela / list inline', size: 'w-3 h-3', example: Eye,          do: 'Subtelne markery przy rekordach. text-text-muted dla neutralnych.',                        dont: 'Nie wstawiaj koloru który ginie na wierszu hover.' },
  { context: 'Modal close',          size: 'w-5 h-5', example: X,            do: 'Górny prawy róg modala. text-text-muted hover→primary.',                                    dont: 'Nie zastępuj „X" tekstem „Zamknij" — konwencja.' },
];

function IconSizeRow({ cls, px, usage, example: Icon }: { cls: string; px: string; usage: string; example: typeof Check }) {
  return (
    <div className="grid grid-cols-[80px_60px_minmax(0,1fr)_44px] gap-3 items-center py-2 border-b border-border-subtle last:border-b-0">
      <code className="text-xs font-mono text-text-primary">{cls}</code>
      <span className="text-[11px] font-mono text-text-muted">{px}</span>
      <p className="text-xs text-text-secondary leading-snug">{usage}</p>
      <div className="flex items-center justify-center w-11 h-11 rounded-lg bg-surface-raised/40 border border-border-subtle">
        <Icon className={`${cls} text-accent-primary`} />
      </div>
    </div>
  );
}

function IconColorRow({ cls, usage, example: Icon }: { cls: string; usage: string; example: typeof Check }) {
  return (
    <div className="grid grid-cols-[200px_minmax(0,1fr)_44px] gap-3 items-center py-2 border-b border-border-subtle last:border-b-0">
      <code className="text-xs font-mono text-text-primary">{cls}</code>
      <p className="text-xs text-text-secondary leading-snug">{usage}</p>
      <div className="flex items-center justify-center w-11 h-11 rounded-lg bg-surface-raised/40 border border-border-subtle">
        <Icon className={`w-4 h-4 ${cls}`} />
      </div>
    </div>
  );
}

function IconUsageCard({ context, size, example: Icon, do: doRule, dont }: { context: string; size: string; example: typeof Check; do: string; dont: string }) {
  return (
    <div className="rounded-xl border border-border bg-surface-raised/40 p-4 space-y-3">
      <div className="flex items-center gap-2.5">
        <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-accent-primary/10 border border-accent-primary/20">
          <Icon className={`${size} text-accent-primary`} />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-text-primary truncate">{context}</p>
          <code className="text-[10px] font-mono text-text-muted">{size}</code>
        </div>
      </div>
      <div className="space-y-1.5 text-xs leading-snug">
        <p className="flex gap-1.5"><span className="text-success font-semibold flex-shrink-0">✓</span><span className="text-text-secondary">{doRule}</span></p>
        <p className="flex gap-1.5"><span className="text-danger font-semibold flex-shrink-0">✗</span><span className="text-text-secondary">{dont}</span></p>
      </div>
    </div>
  );
}

export function IconsSection() {
  return (
    <div className="space-y-8">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        <strong>Lucide React</strong> to jedyna biblioteka ikon. Custom ikony tylko gdy
        Lucide nie ma odpowiednika (np. <code className="px-1 rounded bg-surface-raised text-text-secondary">SkullIcon</code>{' '}
        dla czarnej listy). Używaj zawsze klas Tailwind (<code className="px-1 rounded bg-surface-raised text-text-secondary">w-4 h-4</code>),
        nigdy <code className="px-1 rounded bg-surface-raised text-text-secondary">style={'{{ width: 16 }}'}</code>.
        Stroke pozostaje domyślny Lucide (1.5–2) — nie dostosowuj per ikona.
      </p>

      <Subsection n={1} title="Skala rozmiarów" description="5 rozmiarów do wszystkich kontekstów. w-4 jest defaultem dla buttonów i inputów.">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4">
          {ICON_SIZES.map(s => <IconSizeRow key={s.cls} {...s} />)}
        </div>
      </Subsection>

      <Subsection n={2} title="Konteksty kolorów" description="Token text-* dla neutralnych, accent / status (success/warning/danger/info) dla znaczących.">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4">
          {ICON_COLORS.map(c => <IconColorRow key={c.cls} {...c} />)}
        </div>
      </Subsection>

      <Subsection n={3} title="Zasady użycia per kontekst" description="Match rozmiaru do miejsca. Te same wzorce w całej aplikacji.">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {ICON_USAGE_RULES.map(r => <IconUsageCard key={r.context} {...r} />)}
        </div>
      </Subsection>

      <Subsection n={4} title="Status indicator cluster" description="Wzór z OrderRow w Logistyce — 3–4 ikony w wierszu, gap-0.5, bez dopisków. Title atrybut na hover.">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-text-primary text-sm font-mono">#14403</span>
            <span className="text-text-muted text-xs">80 PLN</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-info/20 text-accent-info ml-auto">W realizacji</span>
          </div>
          <div className="flex items-center gap-0.5">
            <div className="p-0.5 rounded text-success" title="Opłacone"><CreditCard className="w-3 h-3" /></div>
            <div className="p-0.5 rounded text-accent-primary" title="Przesyłka nadana"><Truck className="w-3 h-3" /></div>
            <div className="p-0.5 rounded text-text-disabled" title="Brak faktury"><FileText className="w-3 h-3" /></div>
          </div>
          <p className="text-[11px] text-text-muted mt-3 leading-snug">
            Wszystkie ikony <code className="px-1 rounded bg-surface-raised text-text-secondary">w-3 h-3</code>,
            kontener <code className="px-1 rounded bg-surface-raised text-text-secondary">gap-0.5</code>.
            Aktywny stan: kolor statusowy. Nieaktywny: <code className="px-1 rounded bg-surface-raised text-text-secondary">text-text-disabled</code>.
          </p>
        </div>
      </Subsection>

      <Subsection n={5} title="Buttony z ikoną" description="gap-2 między ikoną a tekstem, ikona zawsze na lewo (RTL nieobsługiwany).">
        <div className="flex flex-wrap items-center gap-3">
          <button className="btn-primary text-sm flex items-center gap-2"><Save className="w-4 h-4" /> Zapisz</button>
          <button className="btn-secondary text-sm flex items-center gap-2"><Edit2 className="w-4 h-4" /> Edytuj</button>
          <button className="btn-ghost text-sm flex items-center gap-2 text-danger"><Trash2 className="w-4 h-4" /> Usuń</button>
          <button className="btn-ghost text-sm flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Wróć</button>
          <button className="btn-ghost text-sm flex items-center gap-1.5"><ChevronDown className="w-3.5 h-3.5" /> Rozwiń</button>
        </div>
      </Subsection>

      <Subsection n={6} title="Custom ikony" description="Tylko gdy Lucide nie ma odpowiednika. Konwencja: components/icons/[Name]Icon.tsx, props { className }.">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4 space-y-2">
          <p className="text-xs text-text-secondary">
            <strong>Aktualne custom ikony:</strong>
          </p>
          <ul className="text-xs text-text-muted space-y-1 ml-4 list-disc">
            <li><code className="text-text-primary">SkullIcon</code> — czarna lista klientów (OrderRow + panel klienta w Wiadomościach)</li>
          </ul>
          <p className="text-[11px] text-text-muted leading-snug pt-2 border-t border-border-subtle">
            Sygnatura: <code className="px-1 rounded bg-surface-raised text-text-secondary">{'{ className?: string }'}</code>.
            SVG inline z <code className="px-1 rounded bg-surface-raised text-text-secondary">currentColor</code> zamiast hardcoded fill,
            żeby działały tokeny <code className="px-1 rounded bg-surface-raised text-text-secondary">text-*</code>.
          </p>
        </div>
      </Subsection>
    </div>
  );
}
